import { FC, ReactNode, createContext, useContext } from 'react';

import { DEFAULT_PAGE_BACKGROUND, pickLhsPageBackground } from '@/utils/theme';
import type { ResolvedThemeMode, ThemeConfig } from '@/utils/theme';

export { DEFAULT_PAGE_BACKGROUND } from '@/utils/theme';

// The LHS (order-info) *page* background is the merchant's raw `colorBackground`
// seed. `colorBackground` still flows into `generateSemanticColors` (so
// cards/dividers keep their derived shades), but the page itself is painted with
// the raw seed rather than the generator's derived companion shade, so it matches
// the exact merchant colour. When the merchant sets no background the LHS falls
// back to the resolved mode's neutral page background — light for a LIGHT/un-themed
// surface, the dark page shade for a DARK one — so a SIMPLE/accent-only dark theme
// no longer leaves the order-info half white (PAA-3188).
//
// Resolution lives in `pickLhsPageBackground` so it is unit-tested directly.
const MerchantBackgroundContext = createContext<string | undefined>(undefined);

export const MerchantBackgroundProvider: FC<{
  theme?: ThemeConfig;
  resolvedMode?: ResolvedThemeMode;
  children: ReactNode;
}> = ({ theme, resolvedMode, children }) => (
  <MerchantBackgroundContext.Provider value={pickLhsPageBackground(theme, resolvedMode)}>
    {children}
  </MerchantBackgroundContext.Provider>
);

// The LHS (order-info) page background color: the raw merchant colorBackground
// seed when the theme carries one, otherwise the resolved mode's neutral page
// background (see `pickLhsPageBackground`). Always returns a color, so consumers
// never need their own fallback.
export const useMerchantBackground = (): string => useContext(MerchantBackgroundContext) ?? DEFAULT_PAGE_BACKGROUND;
